import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { BehaviorSubject, Observable } from 'rxjs';

import { DataService } from '../data/data.service';

const GET_DISCOUNTED_PRODUCTS = gql`
    query GetDiscountedProductsForService {
        discountedProducts {
            product { id }
            discountPercentage
            discountedVariants {
                variantId
                discountPercentage
            }
        }
    }
`;

@Injectable({ providedIn: 'root' })
export class DiscountsService {
    private readonly productSubject = new BehaviorSubject<Map<string, number>>(new Map());
    private readonly variantSubject = new BehaviorSubject<Map<string, number>>(new Map());

    readonly productDiscounts$: Observable<Map<string, number>> = this.productSubject.asObservable();
    readonly variantDiscounts$: Observable<Map<string, number>> = this.variantSubject.asObservable();

    constructor(private dataService: DataService) {
        if (typeof window !== 'undefined') {
            this.reload();
        }
    }

    reload(): void {
        this.dataService
            .queryFresh<{ discountedProducts: Array<any> }>(GET_DISCOUNTED_PRODUCTS)
            .subscribe(({ discountedProducts }) => {
                const productMap = new Map<string, number>();
                const variantMap = new Map<string, number>();
                for (const entry of discountedProducts ?? []) {
                    const productId = entry?.product?.id;
                    const headline = Number(entry?.discountPercentage) || 0;
                    const variantPercents: number[] = [];
                    for (const dv of entry?.discountedVariants ?? []) {
                        const pct = Number(dv?.discountPercentage) || 0;
                        if (dv?.variantId) {
                            variantMap.set(String(dv.variantId), pct);
                        }
                        variantPercents.push(pct);
                    }
                    if (productId) {
                        const max = variantPercents.length > 0
                            ? Math.max(headline, ...variantPercents)
                            : headline;
                        if (max > 0) {
                            productMap.set(String(productId), max);
                        }
                    }
                }
                this.productSubject.next(productMap);
                this.variantSubject.next(variantMap);
            });
    }

    getProductDiscount(productId: string): number {
        return this.productSubject.value.get(String(productId)) ?? 0;
    }

    getVariantDiscount(variantId: string): number {
        return this.variantSubject.value.get(String(variantId)) ?? 0;
    }
}
